/* eslint-disable @typescript-eslint/no-empty-function */
import React, { createContext, useCallback, useEffect } from 'react';
import { Box, Button, Modal, Stack, Typography } from '@mui/material';
import HighlightOffIcon from '@mui/icons-material/HighlightOff';
import {
  DrawerComponent,
  Form,
  ImageDrawerHeaderMeta,
  SectionWithMeta,
  TabContainer,
  TabPanel,
  RenderFuncProps,
  useNotification,
  ResourceDataGrid,
  ConfirmModal,
  DrawerComponentMeta,
} from '@dispatch/web/ui';
import {
  Control,
  FieldErrors,
  FieldValues,
  FormProvider,
  useForm,
  UseFormWatch,
} from 'react-hook-form';
import { useNavigate } from 'react-router-dom';
import {
  Resource,
  Actions,
  Subjects,
  Event,
  Schedule,
  ResourceForm,
  resourceFormDefaults,
  UpsertResource,
  ResultStatus,
  AttributeForm,
  SelectAttributeValuesVarcharsForm,
  resourceFormSchema,
  EditScheduleForm,
  EditEventForm,
  QueryResult,
  MultiSelectOption,
  ITaskMessage,
  TaskRescheduleRequest,
  ResourceMeta,
  ClaimOwnerCapacity,
  CertificationAndLicense,
  AttributeSelection,
} from '@dispatch/shared/types';
import {
  convertDateforMuiDatePicker,
  formatPhoneNumber,
} from '@dispatch/shared';
import { Ability } from '@casl/ability';
import { StyledFieldSet } from './ResourceDetailsStyles';
import { GeneralTab } from './components/General/GeneralTab';
import { CertsAndLicensesTab } from './components/CertsAndLicenses/CertsAndLicensesTab';
import { LocationsTab } from './components/Locations/LocationsTab';
import { TeamsOfficesRoles } from './components/TeamsOfficesRoles/TeamsOfficesRoles';
import { AttributesTab } from './components/Attributes/AttributesTab';
import { yupResolver } from '@hookform/resolvers/yup';
import { Tasks } from './components/ResourceTasks';
import { isEmpty, isNull, isUndefined } from 'lodash';
import { isBefore, isToday } from 'date-fns';
import { ResourceStatusChip } from './components/ResourceStatusChip';
import { ForceReassignTasksDialog } from './components/ForceReassignTasksDialog';
import { isTaskReassignable } from '@dispatch/shared/services';
import { AdditionalDataTab } from './components/AdditionalData/AdditionalDataTab';
import { ResourceHistoryTab } from './history/ResourceHistoryTab';
import { useCompany, useGeneralDataContext } from '@dispatch/shared/hooks';

export type ResourceDetailsProps = {
  data: Resource;
  status: ResultStatus;
  upsertScheduleStatus: ResultStatus;
  upsertEventStatus: ResultStatus;
  upsertResourceStatus: ResultStatus;
  tasks: {
    assigned: { data: ITaskMessage[] | undefined; loading: boolean };
    unassigned: { data: ITaskMessage[] | undefined; loading: boolean };
  };
  onScheduleSave: (values: EditScheduleForm) => void;
  onResourceSave: (values: ResourceForm) => void;
  onEventSave: (values: EditEventForm) => void;
  onTaskSave: (values: { selected: TaskRescheduleRequest[] }) => void; // type will be determined when assigning task feature is implemented
  onForceReassign?: (
    values: { resource: MultiSelectOption },
    selectedTasks?: ITaskMessage[]
  ) => void;
  onCompletePastTasks?: () => void;
  completeTasksStatus?: ResultStatus;
  dispatchStatus?: string;
  permissions: Ability<[Actions, Subjects]> | undefined;
  currentTab: number;
  setTab: (nextTab: number) => void;
  certsAndLicense?: CertificationAndLicense[];
  upsertResource: UpsertResource;
  tabLabels?: string[];
  isNewResource?: boolean;
  disabledTabs?: number[];
  showActivateButton?: boolean;
  onActivateOrDeactivate?: (isActive: boolean) => void;
  eventsQuery?: QueryResult<Event[] | undefined, unknown>;
  schedulesQuery?: QueryResult<Schedule[] | undefined, unknown>;
  efficiencies: MultiSelectOption[];
  navigateToOnClose?: string;
  resourcesData?: any;
  qualifiedState?: {
    showOnlyQualified: boolean;
    setShowOnlyQualified: React.Dispatch<boolean>;
  };
  qualifiedStatus?: ResultStatus;
  resourceMeta?: any;
  setTaskToReassign?: any;
  onStandbySave?: (isStandby: boolean) => void;
  showLocationBasedEvents?: boolean;
  isReadOnly?: boolean | null;
};

export interface SelectedTask {
  selected: TaskRescheduleRequest[];
  selectedAssignedTasks: ITaskMessage[];
}

interface ResourceContextI {
  data: Resource;
  resourceWatch?: UseFormWatch<ResourceForm>;
  upsertResourceStatus?: ResultStatus;
  resourceMeta?: any;
  showLocationBasedEvents?: boolean;
}

export const ResourceContext = createContext<ResourceContextI>({
  data: {} as Resource,
  // eslint-disable-next-line @typescript-eslint/no-empty-function
  upsertResourceStatus: 'success',
});

export function ResourcesDetails({
  data,
  onTaskSave,
  onCompletePastTasks,
  onResourceSave,
  onEventSave,
  onScheduleSave,
  onForceReassign,
  status,
  dispatchStatus,
  upsertScheduleStatus,
  upsertEventStatus,
  upsertResourceStatus,
  completeTasksStatus,
  upsertResource,
  tasks,
  permissions,
  currentTab,
  setTab,
  certsAndLicense,
  efficiencies,
  tabLabels = [
    'GENERAL',
    'LOCATIONS',
    'TEAMS, OFFICES & ROLES',
    'ATTRIBUTES',
    'CERTS & LICENSES',
    'TASKS',
    'ADDITIONAL DATA',
  ],
  isNewResource = false,
  disabledTabs = [],
  showActivateButton = true,
  onActivateOrDeactivate = (isActive: boolean) => {},
  eventsQuery,
  schedulesQuery,
  navigateToOnClose = '/resources',
  resourcesData,
  qualifiedState,
  qualifiedStatus,
  resourceMeta,
  onStandbySave,
  showLocationBasedEvents,
  isReadOnly = false,
}: ResourceDetailsProps) {
  const [isBeingDispatched, setIsBeingDispatched] = React.useState<string>();
  const { notifyError } = useNotification();
  const { featureFlags } = useCompany();
  const { statesOptions } = useGeneralDataContext();
  const [
    showCompletePastTasksConfirmation,
    setShowCompletePastTasksConfirmation,
  ] = React.useState<boolean>(false);

  const navigate = useNavigate();
  const {
    control: taskFormControl,
    formState: taskFormState,
    handleSubmit: taskHandleSubmit,
    setValue: taskSetValue,
    watch: taskWatch,
    reset: taskReset,
  } = useForm<SelectedTask>({
    defaultValues: { selected: [], selectedAssignedTasks: [] },
  });

  const formMethods = useForm<ResourceForm>({
    resolver: yupResolver(resourceFormSchema),
    defaultValues: {
      ...resourceFormDefaults,
    },
  });
  const {
    control: resourceFormControl,
    formState: { errors: resourceFormErrors, isDirty: resourceFormIsDirty },
    handleSubmit: resourceHandleSubmit,
    getValues: resourceGetValues,
    watch: resourceWatch,
    reset: resourceReset,
    setValue: resourceSetValue,
    trigger: resourceTrigger,
  } = formMethods;

  const hasViewResourceAuditPermission = Boolean(
    permissions?.can?.('view', 'resource-audit')
  );

  const effectiveTabLabels = React.useMemo(() => {
    let labels = tabLabels;
    if (featureFlags?.excludeCertsAndLicenses) {
      labels = labels.filter(
        (l) => l.toLocaleUpperCase() !== 'CERTS & LICENSES'
      );
    }
    // History tab is gated on the resource-audit view privilege and is only
    // meaningful for an existing resource (no audit trail for a new one yet).
    if (
      hasViewResourceAuditPermission &&
      !isNewResource &&
      !labels.some((l) => l.toLocaleUpperCase() === 'HISTORY')
    ) {
      labels = [...labels, 'HISTORY'];
    }
    return labels;
  }, [featureFlags, tabLabels, hasViewResourceAuditPermission, isNewResource]);

  const selectedTasks = taskWatch('selected');
  const selectedAssignedTasks = taskWatch('selectedAssignedTasks');
  const handleTaskSave = () => {
    taskHandleSubmit(onTaskSave)();
  };

  const onResourceFormErrors = (resourceErrors: unknown) => {
    // needed to make sure validation errors get highlighted
    resourceTrigger();

    const error = Object.keys(resourceErrors as ResourceForm)[0];

    switch (error) {
      case 'generalInformation':
        setTab(getTabIndex('GENERAL'));
        break;
      case 'locations':
        setTab(getTabIndex('LOCATIONS'));
        break;
      case 'attributes':
        setTab(getTabIndex('ATTRIBUTES'));
        break;
      case 'teamsOfficesRoles':
        setTab(getTabIndex('TEAMS, OFFICES & ROLES'));
        break;
      case 'certsAndLicenses':
        setTab(getTabIndex('CERTS & LICENSES'));
        break;
      case 'additionalData':
        setTab(getTabIndex('ADDITIONAL DATA'));
        break;
    }
    notifyError('Validation Error', 'Please make sure all fields are valid');
  };

  const handleActivateOrDeactivate = (activate: boolean) => {
    onActivateOrDeactivate(activate);
  };

  const firstName = data?.name?.split(' ')[0];
  const hasUpsertPermissions = permissions?.can?.('upsert', 'resource');

  useEffect(() => {
    if (dispatchStatus === 'success') {
      taskSetValue('selected', [], { shouldDirty: false });
      taskSetValue('selectedAssignedTasks', [], { shouldDirty: false });
      taskReset({ selected: [], selectedAssignedTasks: [] });
      setIsBeingDispatched(dispatchStatus);
    } else {
      setIsBeingDispatched(dispatchStatus);
    }
  }, [dispatchStatus, taskSetValue, taskReset]);

  const calculateSumOfCapacities = (
    claimOwnerCapacity?: ClaimOwnerCapacity
  ) => {
    if (!claimOwnerCapacity) {
      return 0;
    }

    return (
      claimOwnerCapacity?.onMonday +
      claimOwnerCapacity?.onTuesday +
      claimOwnerCapacity?.onWednesday +
      claimOwnerCapacity?.onThursday +
      claimOwnerCapacity?.onFriday +
      claimOwnerCapacity?.onSaturday +
      claimOwnerCapacity?.onSunday
    );
  };

  const resourceFormData: ResourceForm = React.useMemo(() => {
    return {
      generalInformation: {
        name: data?.name || '',
        description: data?.description || '',
        externalResourceId:
          data?.companies?.find(
            (company) =>
              company.integrationGUID === data?.company?.integrationGUID
          )?.externalResourceId || '',
        commuteTimeInMin: data?.commuteTimeInMin || 0,
        wipLimit: data?.wipLimit || 0,
        email: data?.email || '',
        phone: data?.phone || '',
        claimCapacity: data?.claimCapacity || 0,
        virtualClaimCapacity: data?.virtualClaimCapacity || 0,
        complexLossClaimCapacity: data?.complexLossClaimCapacity || 0,
        efficiency: data?.efficiency,
        isWorkTeam: data?.isWorkTeam,
        lockImmediately: data?.lockImmediately,
        cwsUserId: data?.cwsUserId || '',
        isAlwaysAvailable: data?.isAlwaysAvailable,
      },
      locations:
        data?.locations?.map((location) => {
          let zoneType = 'exclusionZonesInformation';
          if (
            !isEmpty(location.inclusionZones?.states) ||
            !isEmpty(location.inclusionZones?.counties) ||
            !isEmpty(location.inclusionZones?.zipCodes)
          ) {
            zoneType = 'inclusionZonesInformation';
          }

          return {
            integrationGUID: location?.integrationGUID,
            locationInformation: {
              name: location?.name,
              street: location?.street,
              unit: location?.unit,
              city: location?.city,
              state: location?.state,
              zip: location?.zip,
              county: location?.county,
              lat: location?.lat,
              long: location?.long,
              // need to convert to string for radio buttons to work properly
              isVirtualLocation:
                location?.isVirtualLocation?.toString() ?? 'false',
            },
            serviceInformation: {
              // need to convert date string to local time that works with mui date picker
              startDate: convertDateforMuiDatePicker(location?.startDate),
              endDate: convertDateforMuiDatePicker(location?.endDate) ?? '',
              allowedTravelRadius: location?.allowedTravelRadius,
              monday: location?.monday,
              tuesday: location?.tuesday,
              wednesday: location?.wednesday,
              thursday: location?.thursday,
              friday: location?.friday,
              saturday: location?.saturday,
              sunday: location?.sunday,
              isStartingLocation: location?.isStartingLocation,
              // need to convert to string for radio buttons to work properly
              // needed to be able to set allowedTravelRadius to 0 if isVirtualLocation is true
              isVirtualLocation:
                location?.isVirtualLocation?.toString() ?? 'false',
            },
            exclusionZonesInformation: {
              states: location?.exclusionZones?.states,
              counties: location?.exclusionZones?.counties?.map((county) => ({
                title: county,
                value: county,
              })),
              zipCodes: location?.exclusionZones?.zipCodes,
            },
            inclusionZonesInformation: {
              states: location?.inclusionZones?.states,
              counties: location?.inclusionZones?.counties?.map((county) => ({
                title: county,
                value: county,
              })),
              zipCodes: location?.inclusionZones?.zipCodes,
            },
            zoneType,
          };
        }) || [],
      attributes:
        data?.selectedAttributeDefinitions?.map((attribute): AttributeForm => {
          return {
            integrationGUID: attribute.integrationGUID,
            attributeDefinitionIntegrationGUID:
              attribute.attributeDefinitionIntegrationGUID,
            displayName: attribute.displayName,
            selectedAttributeValuesVarchars:
              attribute.selectedAttributeValuesVarchars.map(
                (value): SelectAttributeValuesVarcharsForm => {
                  return {
                    integrationGUID: value.integrationGUID,
                    selectedAttributeDefinitionIntegrationGUID:
                      attribute.integrationGUID,
                    attributeDefinitionValueIntegrationGUID:
                      value.attributeDefinitionValueIntegrationGUID,
                    title: value.displayName,
                    value: value.attributeDefinitionValueIntegrationGUID,
                    startDate:
                      convertDateforMuiDatePicker(value.startDate) ?? '',
                    endDate: convertDateforMuiDatePicker(value.endDate) ?? '',
                    subAttributes: value?.subAttributes?.map((value) => {
                      return {
                        integrationGUID: value.integrationGUID,
                        selectedAttributeDefinitionIntegrationGUID:
                          attribute.integrationGUID,
                        attributeDefinitionValueIntegrationGUID:
                          value.attributeDefinitionValueIntegrationGUID,
                        title: value.displayName,
                        displayName: value.displayName,
                        value: value.attributeDefinitionValueIntegrationGUID,
                        startDate:
                          convertDateforMuiDatePicker(value.startDate) ?? '',
                        endDate:
                          convertDateforMuiDatePicker(value.endDate) ?? '',
                      };
                    }),
                  };
                }
              ),
            selectedAttributeValuesBools: {
              integrationGUID:
                attribute.selectedAttributeValuesBools[0]?.integrationGUID,
              selectedAttributeDefinitionIntegrationGUID:
                attribute.selectedAttributeValuesBools[0]
                  ?.selectedAttributeDefinitionIntegrationGUID,
              attributeDefinitionValueIntegrationGUID:
                attribute.integrationGUID,
              title: attribute.selectedAttributeValuesBools[0]?.displayName,
              value: attribute.selectedAttributeValuesBools[0]?.value,
              startDate:
                attribute.selectedAttributeValuesBools[0]?.startDate || '',
              endDate: attribute.selectedAttributeValuesBools[0]?.endDate,
            },
          };
        }) || [],
      teamsOfficesRoles: {
        teams: data?.teams?.map((team) => team.integrationGUID),
        offices: data?.offices?.map((office) => office.integrationGUID),
        roles: data?.roles?.map((role) => role.integrationGUID),
        groups: data?.groups?.map((group) => group.integrationGUID),
        isContingent: data?.isContingent || false,
        contingentCompany: data?.contingentCompany,
      },
      certsAndLicenses:
        data?.selectedCertificationsAndLicenses?.map((cert) => {
          return {
            integrationGUID: cert.integrationGUID || '',
            certificationAndLicenseIntegrationGUID:
              cert.certificationAndLicenseIntegrationGUID || '',
            licenseNumber: cert.licenseNumber || '',
            verificationStatus: cert.verificationStatus || '',
            documentCount: cert.documentCount || 0,
            issueDate: cert.issueDate || '',
            expirationDate: cert.expirationDate || '',
          };
        }) || [],
      addSelectedAttributeGUID: '',
      isNewResource: !data?.integrationGUID, // Will be new Resource if integrationGUID exists
      selectedAttributeGroups: data?.selectedAttributeGroups || [],
      attributeFilterSelection:
        data?.attributeFilterSelection ??
        (data?.selectedAttributeGroups?.length
          ? AttributeSelection.Group
          : AttributeSelection.Single),
      claimOwner: {
        onSunday: data.claimOwnerCapacity?.onSunday || 0,
        onMonday: data.claimOwnerCapacity?.onMonday || 0,
        onTuesday: data.claimOwnerCapacity?.onTuesday || 0,
        onWednesday: data.claimOwnerCapacity?.onWednesday || 0,
        onThursday: data.claimOwnerCapacity?.onThursday || 0,
        onFriday: data.claimOwnerCapacity?.onFriday || 0,
        onSaturday: data.claimOwnerCapacity?.onSaturday || 0,
        calculatedSumOfCapacities: calculateSumOfCapacities(
          data.claimOwnerCapacity
        ),
        maxWeeklyCapacity: data.claimOwnerCapacity?.maxWeeklyCapacity || 0,
      },
      // Seed the selector from what is stored. An unspecified mode resolves as STATIC on the
      // server, so it must default to STATIC here -- defaulting to PER_DAY would show a grid for
      // a resource that actually resolves from the legacy scalar.
      fieldCapacityMode: data.fieldCapacityMode ?? 'STATIC',
      // AC6 read-side default: an un-migrated resource (no per-day values yet)
      // shows its legacy claimCapacity across all seven days. This only seeds
      // what is *displayed* -- whether a per-day payload is submitted is decided
      // by the feature flag in ResourceFormHelper.
      fieldCapacityByDay: {
        onSunday: data.fieldCapacityByDay?.onSunday ?? data.claimCapacity ?? 0,
        onMonday: data.fieldCapacityByDay?.onMonday ?? data.claimCapacity ?? 0,
        onTuesday:
          data.fieldCapacityByDay?.onTuesday ?? data.claimCapacity ?? 0,
        onWednesday:
          data.fieldCapacityByDay?.onWednesday ?? data.claimCapacity ?? 0,
        onThursday:
          data.fieldCapacityByDay?.onThursday ?? data.claimCapacity ?? 0,
        onFriday: data.fieldCapacityByDay?.onFriday ?? data.claimCapacity ?? 0,
        onSaturday:
          data.fieldCapacityByDay?.onSaturday ?? data.claimCapacity ?? 0,
      },
    };
  }, [data, data?.company]);

  const claimOwner = resourceWatch('claimOwner');

  const calculateWeeklyCapacity = () => {
    const { maxWeeklyCapacity, calculatedSumOfCapacities, ...rest } =
      claimOwner || {};
    return Object.values(rest || [])
      .map((value) => Number(value))
      .reduce((a, b) => a + b, 0);
  };

  React.useEffect(() => {
    const calculatedCapacities = calculateWeeklyCapacity();

    resourceSetValue(
      'claimOwner.calculatedSumOfCapacities',
      calculatedCapacities
    );
  }, [
    claimOwner,
    claimOwner?.onMonday,
    claimOwner?.onTuesday,
    claimOwner?.onWednesday,
    claimOwner?.onThursday,
    claimOwner?.onFriday,
    claimOwner?.onSaturday,
    claimOwner?.onSunday,
  ]);

  const handleResourceSave = () => {
    resourceHandleSubmit(onResourceSave, onResourceFormErrors)().then(() => {
      resourceReset({}, { keepValues: true });
    });
  };

  useEffect(() => {
    if (!resourceFormIsDirty) {
      resourceReset(resourceFormData);
    }
  }, [resourceFormData, resourceReset]);

  // check if the resource has any old tasks that need to be completed
  const hasOldTasks = React.useMemo(() => {
    if (!tasks?.assigned?.data) return false;

    const oldTasks = tasks?.assigned?.data?.some((task) => {
      if (
        isToday(new Date(task?.appointment || '')) ||
        !isNull(task.completed)
      ) {
        return false;
      }

      return (
        isBefore(new Date(task?.appointment || ''), new Date()) &&
        task.targetQueue !== 'egress'
      );
    });

    return oldTasks;
  }, [tasks]);

  const completeTasksButton = useCallback(
    () => (
      <Button
        disabled={!hasOldTasks}
        loading={completeTasksStatus === 'loading'}
        variant="outlined"
        color="primary"
        onClick={() => setShowCompletePastTasksConfirmation(true)}
        loadingPosition="end"
        sx={{ '& .MuiButton-endIcon': { display: 'none' } }}
      >
        <Box
          component={'span'}
          sx={{
            paddingRight:
              completeTasksStatus === 'loading' ? '20px !important' : '0px',
          }}
        >
          Complete Tasks
        </Box>
      </Button>
    ),
    [completeTasksStatus, hasOldTasks]
  );

  const [showReassignAllModal, setShowReassignAllModal] = React.useState(false);
  const isReassignAllDisabled = (data: ITaskMessage[] | undefined) => {
    return !data?.some(isTaskReassignable);
  };
  const reAssignAllButton = useCallback(() => {
    return (
      <Button
        data-testid="reassign-all-button"
        onClick={() =>
          selectedAssignedTasks.length > 1
            ? setShowReassignAllModal(true)
            : resourceMeta?.setTaskToAssign(selectedAssignedTasks[0])
        }
        variant="outlined"
        color="primary"
        disabled={isReassignAllDisabled(selectedAssignedTasks)}
      >
        Re-assign Selected
      </Button>
    );
  }, [tasks?.assigned?.data?.length, selectedAssignedTasks.length]);

  const renderTasks = React.useCallback(
    (currentTab: number, index: number) => {
      return (
        <TabPanel value={currentTab} index={index}>
          <Stack sx={{ padding: 0 }}>
            <Box
              data-testid="assigned-tasks"
              sx={(theme) => ({
                padding: '24px',
                backgroundColor:
                  theme.palette.mode === 'dark'
                    ? theme.palette.action.hover
                    : '#F4F5FA',
              })}
            >
              <SectionWithMeta
                title={
                  firstName ? `${firstName}'s Assigned Tasks` : 'Assigned Tasks'
                }
                meta={() => {
                  return (
                    <Stack direction={'row'} spacing={1}>
                      {!isReadOnly && reAssignAllButton()}
                      {!isReadOnly && completeTasksButton()}
                    </Stack>
                  );
                }}
              >
                <Tasks
                  data={{
                    data: tasks?.assigned?.data,
                    loading: tasks?.assigned?.loading,
                  }}
                  taskType="assigned"
                  height="320px"
                  permissions={permissions}
                  setSelected={taskSetValue}
                  showAppointment
                  resourceFetchStatus={qualifiedStatus}
                />
              </SectionWithMeta>
            </Box>
            {!featureFlags.qualifiedResourcesFnol && (
              <div data-testid="unassigned-tasks" style={{ padding: '24px' }}>
                <SectionWithMeta
                  title="All Active Unassigned Tasks"
                  subtitle={`Recommended based on ${firstName}’s availability, location, and attributes.`}
                  meta={() => (
                    <Box hidden={!hasUpsertPermissions}>
                      <Button
                        data-testid="assign-to-resource-button"
                        onClick={handleTaskSave}
                        variant="contained"
                        disabled={
                          selectedTasks.length <= 0 ||
                          isBeingDispatched === 'loading' ||
                          isNewResource
                        }
                      >
                        {isBeingDispatched === 'loading'
                          ? `Assigning to ${firstName || ''}`
                          : `Assign to ${firstName || ''}`}
                      </Button>
                    </Box>
                  )}
                >
                  <Tasks
                    data={{
                      data: tasks?.unassigned?.data,
                      loading: tasks?.unassigned?.loading,
                    }}
                    resourceFetchStatus={qualifiedStatus}
                    taskType="unassigned"
                    height="340px"
                    setSelected={taskSetValue}
                    getSelected={selectedTasks}
                    permissions={permissions}
                  />
                </SectionWithMeta>
              </div>
            )}
          </Stack>
        </TabPanel>
      );
    },
    [
      qualifiedStatus,
      qualifiedState?.showOnlyQualified,
      tasks,
      taskFormState.isDirty,
      selectedAssignedTasks.length,
    ]
  );

  const renderGeneral = (
    currentTab: number,
    control: Control<FieldValues>,
    errors: FieldErrors,
    index: number,
    isReadOnly = false
  ) => {
    return (
      <TabPanel value={currentTab} index={index}>
        <GeneralTab
          isReadOnly={isReadOnly}
          eventsQuery={eventsQuery}
          schedulesQuery={schedulesQuery}
          onEventSave={onEventSave}
          onScheduleSave={onScheduleSave}
          upsertEventStatus={upsertEventStatus}
          upsertScheduleStatus={upsertScheduleStatus}
          control={control}
          permissions={permissions}
          errors={errors}
          isNewResource={isNewResource}
          efficiencies={efficiencies}
          claimOwnerCapacity={data?.claimOwnerCapacity}
          fieldCapacityByDay={data?.fieldCapacityByDay}
          isFieldCapacityByDayEnabled={
            featureFlags?.fieldCapacityByDayEnabled ?? false
          }
          setValue={resourceSetValue}
          isAlwaysAvailable={resourceGetValues(
            'generalInformation.isAlwaysAvailable'
          )}
        />
      </TabPanel>
    );
  };

  const renderLocations = (
    currentTab: number,
    control: Control<FieldValues>,
    errors: FieldErrors,
    index: number,
    isFormDirty: boolean,
    isReadOnly = false
  ) => {
    return (
      <TabPanel value={currentTab} index={index}>
        <LocationsTab
          control={control}
          permissions={permissions}
          watch={resourceWatch}
          errors={errors && errors['locations'] ? errors['locations'] : null}
          statesOptions={statesOptions}
          upsertResource={upsertResource}
          resourceSetValue={resourceSetValue}
          resourceErrors={resourceFormErrors}
          resourceFormIsDirty={resourceFormIsDirty}
          isReadOnly={isReadOnly}
        />
      </TabPanel>
    );
  };

  const renderCertsAndLicenses = (
    currentTab: number,
    index: number,
    isReadOnly = false
  ) => {
    return (
      <TabPanel value={currentTab} index={index}>
        <CertsAndLicensesTab
          isReadOnly={isReadOnly}
          certsAndLicenseResponse={certsAndLicense}
          upsertResource={upsertResource}
          upsertResourceStatus={upsertResourceStatus}
          permissions={permissions}
          setValue={resourceSetValue}
        />
      </TabPanel>
    );
  };

  const renderTeamOfficesRoles = React.useCallback(
    (
      currentTab: number,
      control: Control<FieldValues>,
      errors: FieldErrors,
      index: number,
      isReadOnly?: boolean
    ) => {
      return (
        <TabPanel value={currentTab} index={index}>
          <TeamsOfficesRoles
            control={control}
            errors={
              errors && errors['teamsOfficesRoles']
                ? errors['teamsOfficesRoles']
                : null
            }
            permissions={permissions}
            watch={resourceWatch}
            isReadOnly={isReadOnly}
          />
        </TabPanel>
      );
    },
    [isReadOnly]
  );

  const renderAttributes = (
    currentTab: number,
    control: Control<FieldValues>,
    errors: FieldErrors,
    index: number,
    isReadOnly = false
  ) => {
    return (
      <TabPanel value={currentTab} index={index}>
        <AttributesTab
          isReadOnly={isReadOnly}
          control={control}
          permissions={permissions}
          watch={resourceWatch}
          getValues={resourceGetValues}
          errors={errors && errors['attributes'] ? errors['attributes'] : null}
          setValue={resourceSetValue}
        />
      </TabPanel>
    );
  };

  const renderAdditionalData = (
    currentTab: number,
    index: number,
    isReadOnly = false
  ) => {
    return (
      <TabPanel value={currentTab} index={index}>
        <AdditionalDataTab
          permissions={permissions}
          upsertResource={upsertResource}
          upsertResourceStatus={upsertResourceStatus}
          isReadOnly={isReadOnly}
        />
      </TabPanel>
    );
  };

  const renderHistory = (currentTab: number, index: number) => {
    if (index < 0) {
      return null;
    }
    return (
      <TabPanel value={currentTab} index={index}>
        <ResourceHistoryTab
          resourceId={data?.integrationGUID ?? ''}
          permissions={permissions}
          certsAndLicense={certsAndLicense}
        />
      </TabPanel>
    );
  };

  const getTabIndex = (tab: string) => {
    // derive index from effectiveTabLabels so indices stay correct when tabs are filtered
    return effectiveTabLabels.findIndex(
      (label) => label.toLocaleUpperCase() === tab.toLocaleUpperCase()
    );
  };

  const completePastTasks = () => {
    onCompletePastTasks?.();
    setShowCompletePastTasksConfirmation(false);
  };

  const [showStandbyConfirmationModal, setShowStandbyConfirmationModal] =
    React.useState(false);

  const renderStandbyConfirmationModal = useCallback(() => {
    return (
      <ConfirmModal
        onConfirm={() => {
          onStandbySave?.(!data.isStandby);
          setShowStandbyConfirmationModal(false);
        }}
        onDeny={() => setShowStandbyConfirmationModal(false)}
        confirmContent={
          data.isStandby
            ? `Are you sure you want to remove this resource from standby?`
            : `Are you sure you want to set this resource to standby?`
        }
        confirmTitle="Set Resource to Standby"
        modalType={'custom'}
        confirmButtonLabel={
          data.isStandby ? 'Remove from Standby' : 'Set to Standby'
        }
        confirmColor="warning"
      />
    );
  }, [data.isStandby, onStandbySave, setShowStandbyConfirmationModal]);

  const renderStandbyButton = useCallback(() => {
    return (
      <Stack px={1}>
        <Button
          data-testid="standBy-button"
          type="button"
          variant="outlined"
          color="warning"
          onClick={() => setShowStandbyConfirmationModal(true)}
          sx={{
            fontWeight: '700',
            lineHeight: '1.3750em',
            letterSpacing: '0.029em',
          }}
        >
          {data.isStandby ? 'Remove from Standby' : 'Set to Standby'}
        </Button>
      </Stack>
    );
  }, [data.isStandby, setShowStandbyConfirmationModal]);

  const renderQualifiedResourcesBackButton = () => {
    return (
      <Stack px={1}>
        <Button
          data-testid="qualified-resources-button"
          type="button"
          variant="outlined"
          color="warning"
          onClick={() => navigate('/tasks/add-qualified')}
          sx={{
            fontWeight: '700',
            lineHeight: '1.3750em',
            letterSpacing: '0.029em',
          }}
        >
          Back
        </Button>
      </Stack>
    );
  };

  return (
    <>
      <FormProvider {...formMethods}>
        <DrawerComponent
          imageUrl=" "
          showButtons={!!hasUpsertPermissions}
          showSaveButton={!isReadOnly}
          showDeactivateButton={
            showActivateButton &&
            !!data.isActive &&
            !!hasUpsertPermissions &&
            !isReadOnly
          }
          showActivateButton={
            showActivateButton &&
            !data.isActive &&
            !!hasUpsertPermissions &&
            !isReadOnly
          }
          onSave={handleResourceSave}
          saveDisabled={
            !resourceFormIsDirty || upsertResourceStatus === 'loading'
          }
          onClose={() => {
            isReadOnly
              ? navigate('/tasks/add-qualified')
              : navigate(navigateToOnClose);
          }}
          title={data?.name}
          renderLeftSideButtons={[
            !isReadOnly
              ? renderStandbyButton()
              : renderQualifiedResourcesBackButton(),
          ]}
          renderTabs={
            <TabContainer
              tabLabels={effectiveTabLabels}
              setTab={setTab}
              currentTab={currentTab}
              disabledTabs={disabledTabs}
            />
          }
          metas={[
            <ImageDrawerHeaderMeta
              title="Resource ID"
              subtitle={data?.companies?.[0]?.externalResourceId}
            />,
            <ImageDrawerHeaderMeta title="EMAIL" subtitle={data?.email} />,
            <ImageDrawerHeaderMeta
              title="PHONE NUMBER"
              subtitle={formatPhoneNumber(data?.phone || '')}
            />,
            <Box sx={{ flexGrow: 1 }} />, //needed for spacing
            <DrawerComponentMeta
              component={
                <Stack direction="row" gap={1}>
                  <ResourceStatusChip
                    isActive={!!data.isActive}
                    isStandby={!!data.isStandby}
                    status={status}
                  />
                </Stack>
              }
            />,
          ]}
          isActive={!!data.isActive}
          sx={{
            width: '65vw',
            '& .MuiBox-root': {
              padding: 0,
            },
          }}
          onActivate={() => handleActivateOrDeactivate(true)}
          onDeactivate={() => handleActivateOrDeactivate(false)}
          isStandby={data.isStandby}
        >
          {data && (
            <ResourceContext.Provider
              value={{
                data,
                resourceWatch,
                upsertResourceStatus: upsertResourceStatus || 'success',
                resourceMeta,
                showLocationBasedEvents,
              }}
            >
              <Form<ResourceForm>
                formId="resourceForm"
                errors={resourceFormErrors}
                control={resourceFormControl}
                handleSubmit={resourceHandleSubmit}
                render={({ control, errors }: RenderFuncProps) => {
                  return (
                    <div style={{ width: '100%' }}>
                      <StyledFieldSet disabled={!hasUpsertPermissions}>
                        {renderGeneral(
                          currentTab,
                          control,
                          errors,
                          getTabIndex('GENERAL'),
                          Boolean(isReadOnly)
                        )}
                        {renderLocations(
                          currentTab,
                          control,
                          errors,
                          getTabIndex('LOCATIONS'),
                          resourceFormIsDirty,
                          Boolean(isReadOnly)
                        )}
                        {renderTeamOfficesRoles(
                          currentTab,
                          control,
                          errors,
                          getTabIndex('TEAMS, OFFICES & ROLES'),
                          Boolean(isReadOnly)
                        )}
                        {renderAttributes(
                          currentTab,
                          control,
                          errors,
                          getTabIndex('ATTRIBUTES'),
                          Boolean(isReadOnly)
                        )}
                        {!featureFlags?.excludeCertsAndLicenses &&
                          renderCertsAndLicenses(
                            currentTab,
                            getTabIndex('CERTS & LICENSES'),
                            Boolean(isReadOnly)
                          )}
                        {renderTasks(currentTab, getTabIndex('TASKS'))}
                        {renderAdditionalData(
                          currentTab,
                          getTabIndex('ADDITIONAL DATA'),
                          Boolean(isReadOnly)
                        )}
                        {hasViewResourceAuditPermission &&
                          renderHistory(currentTab, getTabIndex('HISTORY'))}
                      </StyledFieldSet>
                    </div>
                  );
                }}
              />
              <ReassignModal
                resources={resourcesData}
                resourceMeta={{
                  ...resourceMeta,
                  showOnlyQualified: qualifiedState?.showOnlyQualified,
                  setShowOnlyQualified: qualifiedState?.setShowOnlyQualified,
                }}
              />
              <ForceReassignTasksDialog
                showModal={showReassignAllModal}
                permissions={permissions}
                handleClose={() => setShowReassignAllModal(false)}
                onForceReassign={onForceReassign}
                selectedTasks={selectedAssignedTasks}
              />
              {showCompletePastTasksConfirmation && (
                <>
                  <ConfirmModal
                    onConfirm={completePastTasks}
                    onDeny={() => setShowCompletePastTasksConfirmation(false)}
                    confirmContent="Are you sure you want to complete past tasks?"
                    confirmTitle="Complete past tasks"
                    modalType={'complete'}
                  />
                  <Form<ResourceForm>
                    formId="resourceForm"
                    errors={resourceFormErrors}
                    control={resourceFormControl}
                    handleSubmit={resourceHandleSubmit}
                    render={({ control, errors }: RenderFuncProps) => {
                      return (
                        <div style={{ width: '100%' }}>
                          <StyledFieldSet disabled={!hasUpsertPermissions}>
                            {renderGeneral(
                              currentTab,
                              control,
                              errors,
                              getTabIndex('GENERAL')
                            )}
                            {renderLocations(
                              currentTab,
                              control,
                              errors,
                              getTabIndex('LOCATIONS'),
                              resourceFormIsDirty
                            )}
                            {renderTeamOfficesRoles(
                              currentTab,
                              control,
                              errors,
                              getTabIndex('TEAMS, OFFICES & ROLES')
                            )}
                            {renderAttributes(
                              currentTab,
                              control,
                              errors,
                              getTabIndex('ATTRIBUTES')
                            )}
                            {!featureFlags?.excludeCertsAndLicenses &&
                              renderCertsAndLicenses(
                                currentTab,
                                getTabIndex('CERTS & LICENSES')
                              )}
                          </StyledFieldSet>
                        </div>
                      );
                    }}
                  />
                  <ReassignModal
                    resources={resourcesData}
                    resourceMeta={{
                      ...resourceMeta,
                      showOnlyQualified: qualifiedState?.showOnlyQualified,
                      setShowOnlyQualified:
                        qualifiedState?.setShowOnlyQualified,
                    }}
                  />
                  <ForceReassignTasksDialog
                    showModal={showReassignAllModal}
                    permissions={permissions}
                    handleClose={() => setShowReassignAllModal(false)}
                    onForceReassign={onForceReassign}
                    selectedTasks={selectedAssignedTasks}
                  />
                  {showCompletePastTasksConfirmation && (
                    <ConfirmModal
                      onConfirm={completePastTasks}
                      onDeny={() => setShowCompletePastTasksConfirmation(false)}
                      confirmContent="Are you sure you want to complete past tasks?"
                      confirmTitle="Complete past tasks"
                      modalType={'complete'}
                    />
                  )}
                </>
              )}
            </ResourceContext.Provider>
          )}
        </DrawerComponent>
      </FormProvider>
      {showStandbyConfirmationModal && renderStandbyConfirmationModal()}
    </>
  );
}

const style = {
  position: 'absolute',
  top: '50%',
  left: '50%',
  transform: 'translate(-50%, -50%)',
  width: 800,
  height: 500,
  bgcolor: 'background.paper',
  boxShadow: 24,
  p: '0px 32px 67px 32px',
};

const ReassignModal = ({
  resources,
  resourceMeta,
}: {
  resources: any;
  resourceMeta: ResourceMeta;
}) => {
  return (
    <Modal
      open={!isUndefined(resourceMeta?.taskToAssign)}
      onClose={() => resourceMeta?.setTaskToAssign?.(undefined)}
    >
      <Box sx={style}>
        <Stack>
          <HighlightOffIcon
            sx={{
              '&:hover': { color: 'red' },
              marginLeft: '98%',
              marginTop: '2%',
            }}
            onClick={() => resourceMeta?.setTaskToAssign?.(undefined)}
          />
        </Stack>
        <Stack>
          <Typography variant="subtitle1">
            Re-assigning: {resourceMeta?.taskToAssign?.claimNumber}
          </Typography>
        </Stack>
        {!isUndefined(resourceMeta?.taskToAssign) && (
          <ResourceDataGrid resources={resources} resourceMeta={resourceMeta} />
        )}
      </Box>
    </Modal>
  );
};
